// src/cockpit4/components/GuestPreviewOverlay.tsx
// ============================================
// GuestPreviewOverlay - 游客只读预览层（v6.40 游客全览·只能看不能使用）
//
// 覆盖在真实 workspace 之上：
//   - 不再用磨砂面把功能遮死，改为「界面全可见、交互全拦截」：
//     游客能清楚看到系统各模块长什么样（吸引用户），但点不动、输不了、用不了
//   - 透明拦截层吃掉所有点击/滚动，点击任意位置弹出登录引导（自然转化漏斗）
//   - 顶部"游客预览模式"横幅 + 右下角"登录解锁完整功能"悬浮入口
//
// 说明：本层是前端交互保护；后端 server.py 已有游客只读硬边界（写/LLM 403）。
//       游客会话不写审计日志；财务/设置由 WorkspaceAccessGate 独立加密锁控制。
// ============================================

import { useState } from "react";
import { motion } from "framer-motion";
import { EyeOff, LogIn, MousePointerClick } from "lucide-react";
import type { WorkspaceId } from "../store";
import LoginModal from "./LoginModal";

// 工作区名映射（与 WorkspaceHeader4 TABS 对齐）
const WORKSPACE_NAMES: Record<WorkspaceId, string> = {
  ceo: "总控台",
  marketing: "营销中心",
  media_studio: "内容工厂",
  media_workbench: "自媒体",
  crm: "客户中心",
  knowledge: "知识中心",
  projects: "AI 超市",
  finance: "财务中心",
  workflow: "经营中心",
  geodso: "经营中心",
  boss_view: "老板视图",
  settings: "设置",
};

export default function GuestPreviewOverlay({ workspaceId }: { workspaceId: WorkspaceId }) {
  const [loginOpen, setLoginOpen] = useState(false);

  return (
    <>
      {/* 透明交互拦截层：界面可见，但所有点击/输入/滚轮都被吞掉，只触发登录引导 */}
      <div
        className="absolute inset-0 z-40 cursor-not-allowed"
        style={{ background: "transparent" }}
        onClick={() => setLoginOpen(true)}
        onWheel={(e) => e.preventDefault()}
        onKeyDown={(e) => e.preventDefault()}
        onMouseDown={(e) => e.preventDefault()}
      >
        {/* 顶部横幅：游客预览模式 */}
        <div className="absolute top-0 left-0 right-0 flex items-center gap-2 px-4 py-2 bg-[#0B1220]/85 backdrop-blur-xl border-b border-amber-500/20 z-10">
          <EyeOff size={12} className="text-amber-400" />
          <span className="text-[10px] font-mono text-amber-300 uppercase tracking-widest">
            游客预览模式
          </span>
          <span className="text-[10px] text-gray-500">
            · {WORKSPACE_NAMES[workspaceId]} 展示中 · 只读不可操作
          </span>
          <span className="ml-auto text-[9px] font-mono text-gray-600">登录后解锁全部功能</span>
        </div>

        {/* 右下角悬浮登录入口（不遮挡界面主体） */}
        <motion.button
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, ease: "easeOut" }}
          onClick={(e) => {
            e.stopPropagation();
            setLoginOpen(true);
          }}
          onMouseDown={(e) => e.stopPropagation()}
          className="absolute bottom-5 right-5 z-20 flex items-center gap-2 px-4 py-2.5 rounded-full bg-gradient-to-r from-blue-500 to-emerald-500 text-white text-[12px] font-medium hover:from-blue-400 hover:to-emerald-400 transition-all shadow-lg shadow-blue-500/30 cursor-pointer"
        >
          <LogIn size={13} />
          登录解锁完整功能
        </motion.button>

        {/* 左下角轻提示：说明当前是只读预览 */}
        <div className="absolute bottom-5 left-5 z-20 flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-black/40 backdrop-blur-md border border-white/[0.06] text-[10px] font-mono text-gray-400 pointer-events-none">
          <MousePointerClick size={10} className="text-cyan-400" />
          只能看 · 不能用 · 点击任意处登录体验
        </div>
      </div>

      {loginOpen && <LoginModal onClose={() => setLoginOpen(false)} />}
    </>
  );
}
